import { ChatResponse } from '../types/chat';

export type ConnectionStatus = 'disconnected' | 'connecting' | 'connected' | 'reconnecting';

class ChatService {
  private ws: WebSocket | null = null;
  private messageQueue: string[] = [];
  private isConnecting = false;
  private isDisconnecting = false;
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 5;
  private reconnectTimeout: NodeJS.Timeout | null = null;
  private onMessageCallback: ((response: ChatResponse) => void) | null = null;
  private onErrorCallback: ((error: Error) => void) | null = null;
  private onStatusChangeCallback: ((status: ConnectionStatus) => void) | null = null;
  private apiKey: string | null = null;
  private connectionStatus: ConnectionStatus = 'disconnected';

  setApiKey(apiKey: string) {
    this.apiKey = apiKey;
  }

  private setStatus(status: ConnectionStatus) {
    this.connectionStatus = status;
    this.onStatusChangeCallback?.(status);
  }

  getStatus(): ConnectionStatus {
    return this.connectionStatus;
  }

  connect() {
    if (this.ws?.readyState === WebSocket.OPEN) {
      console.log('[ChatService] WebSocket is already connected');
      return;
    }

    if (this.isConnecting) {
      console.log('[ChatService] WebSocket connection is already in progress');
      return;
    }

    if (!this.apiKey) {
      console.error('[ChatService] No API key set');
      this.onErrorCallback?.(new Error('API key not set'));
      return;
    }

    this.isConnecting = true;
    this.isDisconnecting = false;
    this.setStatus(this.reconnectAttempts > 0 ? 'reconnecting' : 'connecting');

    try {
      const wsProtocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
      const wsHost = import.meta.env.DEV ? 'localhost:8080' : window.location.host;
      const wsUrl = `${wsProtocol}//${wsHost}/api/chat`;
      
      if (this.ws) {
        this.ws.close();
        this.ws = null;
      }

      this.ws = new WebSocket(wsUrl);

      this.ws.onopen = () => {
        console.log('[ChatService] WebSocket connected, sending auth');
        this.setStatus('connected');
        this.isConnecting = false;
        this.reconnectAttempts = 0;
        // Send authentication message
        this.ws?.send(JSON.stringify({ api_key: this.apiKey }));
      };

      this.ws.onmessage = (event) => {
        if (this.isDisconnecting) {
          console.log('[ChatService] Ignoring message - disconnecting');
          return;
        }
        
        try {
          const data = event.data;
          if (typeof data !== 'string') {
            throw new Error('Received non-text message');
          }
          
          console.log('[ChatService] Received message:', data);
          const response = JSON.parse(data) as ChatResponse;
          console.log('[ChatService] Parsed response:', response);

          // Handle server error responses
          if ('error' in response) {
            console.error('[ChatService] Server error:', response.error);
            this.onErrorCallback?.(new Error(response.error));
            
            if ('should_disconnect' in response && response.should_disconnect) {
              console.log('[ChatService] Server requested disconnect');
              this.disconnect();
              return;
            }
          }
          
          // After successful auth, process queued messages
          if (!('error' in response) && this.messageQueue.length > 0) {
            console.log('[ChatService] Auth successful, processing queue');
            this.processMessageQueue();
          }
          
          if (this.onMessageCallback) {
            this.onMessageCallback(response);
          } else {
            console.warn('[ChatService] No message callback registered');
          }
        } catch (err) {
          console.error('[ChatService] Failed to parse message:', err);
          this.onErrorCallback?.(new Error('Failed to parse message'));
        }
      };

      this.ws.onerror = (event) => {
        if (this.isDisconnecting) return;
        
        console.error('[ChatService] WebSocket error:', event);
        this.isConnecting = false;
        this.onErrorCallback?.(new Error('WebSocket connection error'));
      };

      this.ws.onclose = (event) => {
        if (this.isDisconnecting) {
          console.log('[ChatService] WebSocket closed gracefully');
          this.ws = null;
          this.setStatus('disconnected');
          return;
        }

        console.log(`[ChatService] WebSocket closed unexpectedly: ${event.code} ${event.reason}`);
        this.isConnecting = false;
        this.ws = null;
        this.setStatus('disconnected');
        
        if (this.reconnectAttempts < this.maxReconnectAttempts) {
          this.reconnectAttempts++;
          const delay = Math.min(1000 * Math.pow(2, this.reconnectAttempts), 30000); // Cap at 30 seconds
          console.log(`[ChatService] Attempting to reconnect (${this.reconnectAttempts}/${this.maxReconnectAttempts}) in ${delay}ms...`);
          
          // Clear any existing reconnect timeout
          if (this.reconnectTimeout) {
            clearTimeout(this.reconnectTimeout);
          }
          
          this.reconnectTimeout = setTimeout(() => {
            this.reconnectTimeout = null;
            this.connect();
          }, delay);
        } else {
          this.onErrorCallback?.(new Error('Maximum reconnection attempts reached'));
        }
      };
    } catch (err) {
      console.error('[ChatService] Failed to create WebSocket:', err);
      this.isConnecting = false;
      this.setStatus('disconnected');
      this.onErrorCallback?.(new Error('Failed to create WebSocket connection'));
    }
  }

  disconnect() {
    if (!this.ws) {
      console.log('[ChatService] No WebSocket connection to disconnect');
      return;
    }

    console.log('[ChatService] Disconnecting WebSocket');
    this.isDisconnecting = true;
    
    // Clear any pending reconnect timeout
    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
      this.reconnectTimeout = null;
    }
    
    // Close the connection gracefully
    if (this.ws.readyState === WebSocket.OPEN) {
      this.ws.close(1000, 'Client disconnecting');
    } else {
      this.ws = null;
    }
    
    this.setStatus('disconnected');
  }

  send(message: string) {
    if (!message.trim()) {
      console.warn('[ChatService] Attempted to send empty message');
      return;
    }

    const messageData = {
      message: message.trim()
    };

    if (this.ws?.readyState === WebSocket.OPEN) {
      try {
        console.log('[ChatService] Sending message:', messageData);
        this.ws.send(JSON.stringify(messageData));
      } catch (err) {
        console.error('[ChatService] Failed to send message:', err);
        this.onErrorCallback?.(new Error('Failed to send message'));
      }
    } else {
      console.log('[ChatService] WebSocket not ready, queueing message');
      this.messageQueue.push(JSON.stringify(messageData));
      this.connect();
    }
  }

  private processMessageQueue() {
    console.log(`[ChatService] Processing message queue (${this.messageQueue.length} messages)`);
    while (this.messageQueue.length > 0) {
      const message = this.messageQueue.shift();
      if (message) {
        console.log('[ChatService] Sending queued message:', message);
        if (this.ws?.readyState === WebSocket.OPEN) {
          this.ws.send(message);
        }
      }
    }
  }

  onMessage(callback: (response: ChatResponse) => void) {
    console.log('[ChatService] Registering message callback');
    this.onMessageCallback = callback;
  }

  onError(callback: (error: Error) => void) {
    console.log('[ChatService] Registering error callback');
    this.onErrorCallback = callback;
  }

  onStatusChange(callback: (status: ConnectionStatus) => void) {
    console.log('[ChatService] Registering status change callback');
    this.onStatusChangeCallback = callback;
  }
}

export const chatService = new ChatService(); 